import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, CircularProgress, Typography } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useOperatorState } from './hooks/useOperatorState';
import { useCatalogSync } from './hooks/useCatalogSync';
import { useDiagnosticsSummary } from './hooks/useDiagnosticsSummary';
import { useBundleRebuild } from './components/Diagnostics/useBundleRebuild';
import { LiveStatus, PublishState } from './operatorState';
import { deriveChecklistSteps } from './deriveChecklistSteps';
import { useBillingTrial } from '../../hooks/useBillingTrial';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import styles from './HomeScreen.module.scss';
import { bridgeBillingOpenBilling } from '../../slices/BridgeSlice';
import SetupWelcome from './components/SetupWelcome';
import SetupIncompleteBanner from './components/SetupIncompleteBanner';
import OperatorDashboard from './components/OperatorDashboard';
import PublishProgressDialog from './components/PublishProgressDialog';
import {
  useOperationDialogVisibility,
} from './components/PublishProgressDialog/useOperationDialogVisibility';
import { notifyLivePublishSuccess } from './livePublishEvents';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import {
  supportsMarketplacePluginBridgeShape,
  supportsPluginArtifactMutationSnapshot,
} from '../shell/pluginCapabilities';
import { shouldShowOperatorDashboard } from './homeMode';

function HomeStateLoading({ label }) {
  return (
    <div
      className={styles.homeStateLoading}
      data-testid="home-state-loading"
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      <span className={styles.visuallyHidden}>{label}</span>
      <div className={styles.loadingIntro} aria-hidden="true" />
      <div className={styles.loadingMetrics} aria-hidden="true">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className={styles.loadingMetricCard} />
        ))}
      </div>
    </div>
  );
}

HomeStateLoading.propTypes = {
  label: PropTypes.string.isRequired,
};

const HomeScreen = () => {
  const localizeMessage = useLocalizedMessage();
  const shopId = useSelector((state) => state.app?.currentShop);
  const { isUpdatingSearch, isFetched: isSettingsFetched } = useSelector((state) => state.settings);
  const isServiceUpdating = isUpdatingSearch || !isSettingsFetched;
  const { isTrial, trialDaysLeft } = useBillingTrial();
  const { operatorState, isLoading: isOperatorLoading, pluginSnapshot } = useOperatorState();
  const catalogSync = useCatalogSync();
  const publishBuild = useBundleRebuild();
  const publishDialogStatus = publishBuild.status;
  const publishDialog = useOperationDialogVisibility(publishDialogStatus);

  useEffect(() => {
    if (publishDialog.active && publishDialogStatus === 'success') {
      notifyLivePublishSuccess();
    }
  }, [publishDialog.active, publishDialogStatus]);

  const startPublish = useCallback(() => {
    if (typeof publishBuild.reset === 'function') {
      publishBuild.reset();
    }
    publishDialog.begin();
    return publishBuild.startBuildAndPromote(shopId);
  }, [publishBuild, publishDialog, shopId]);

  const effectiveOperatorState = useMemo(() => {
    if (publishBuild.status === 'queued' || publishBuild.status === 'running') {
      return { ...operatorState, publishState: PublishState.BUILDING };
    }
    if (publishBuild.status === 'failed') {
      return { ...operatorState, publishState: PublishState.ERROR };
    }
    if (publishBuild.status === 'success') {
      return { ...operatorState, publishState: PublishState.PUBLISHED, live: LiveStatus.ACTIVE };
    }
    return operatorState;
  }, [operatorState, publishBuild.status]);

  const diagnosticsEventsEnabled = pluginSnapshot?.capabilities?.diagnosticEventsV1 === true;
  const { summary: pluginDiagnosticsSummary } = useDiagnosticsSummary({ enabled: diagnosticsEventsEnabled });
  const history = useHistory();

  // Show a "Setup incomplete" nudge when the user bypassed the wizard.
  // These fields come from the shell session via ShellAppContextProvider —
  // HomeScreen never reads shellSessionStorage directly.
  const {
    onboardingWizardCompleted,
    wizardBypass,
    productProfile,
    capabilities: shellCapabilities,
    runtime,
  } = useShellAppContext();
  const liveArtifactMutationCapability = supportsPluginArtifactMutationSnapshot(pluginSnapshot)
    || (
      shellCapabilities?.artifactMutationV1 === true
      && supportsMarketplacePluginBridgeShape(pluginSnapshot)
    );
  const artifactMutationSupported = productProfile?.id !== 'plugin' || (
    (shellCapabilities?.artifactMutationV1 === true || shellCapabilities?.sharedAdminRuntimeV1 === true)
    && liveArtifactMutationCapability
    && runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE) === true
  );
  const showIncompleteSetupBanner = !!(
    onboardingWizardCompleted === false
    && wizardBypass === true
  );

  const liveIndexing = catalogSync.isDegraded ? null : {
    status: catalogSync.status,
    progress: catalogSync.progress,
    activeIndexPresent: catalogSync.activeIndexPresent,
    error: catalogSync.error,
    isLoading: catalogSync.isLoading,
    isStarting: catalogSync.isStarting,
    lastSuccessAt: catalogSync.lastSuccessAt,
    startedAt: catalogSync.startedAt,
    finishedAt: catalogSync.finishedAt,
    indexedCount: catalogSync.indexedCount,
    totalCount: catalogSync.totalCount,
  };

  const dispatch = useDispatch();

  // Derive onboarding checklist once so we can route between pre- and
  // post-setup home screens. isSetupComplete === true means the merchant
  // has finished the 4-step onboarding (mode chosen, integration health
  // known, preview built, live active).
  const checklist = useMemo(
    () => deriveChecklistSteps(effectiveOperatorState),
    [effectiveOperatorState],
  );
  const showOperatorDashboard = useMemo(
    () => shouldShowOperatorDashboard(effectiveOperatorState, checklist),
    [effectiveOperatorState, checklist],
  );

  // Route-level guard: shopId is required for all operator state.
  // In embedded/bridge mode this can be delayed until the runtime
  // propagates the shop context — show an explicit waiting state.
  if (!shopId) {
    return (
      <div data-testid="home-waiting">
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="320px">
          <CircularProgress size={48} />
          <Typography variant="body2" color="textSecondary" className={styles.waitingHint}>
            {localizeMessage('operator.shell.waiting_for_store')}
          </Typography>
        </Box>
      </div>
    );
  }

  // The unresolved plugin lane intentionally maps to EMPTY_OPERATOR_STATE.
  // Do not let that technical placeholder select the setup experience: wait
  // until Home can choose the real setup or operating mode once and keep it.
  if (isOperatorLoading) {
    return (
      <div
        data-testid="home-shell"
        data-setup-complete="pending"
        data-state="loading"
      >
        <HomeStateLoading label={localizeMessage('operator.home.loading')} />
      </div>
    );
  }

  const trialChip = isTrial ? (
    <Chip
      label={
        trialDaysLeft > 0
          ? localizeMessage('operator.welcome.trial_active', { days: trialDaysLeft })
          : localizeMessage('operator.welcome.trial_ended')
      }
      onClick={productProfile?.id === 'plugin'
        ? () => dispatch(bridgeBillingOpenBilling())
        : undefined}
      clickable={productProfile?.id === 'plugin'}
      className={trialDaysLeft > 0 ? styles.trialChipActive : styles.trialChipEnded}
    />
  ) : null;

  return (
    <div data-testid="home-shell" data-setup-complete={String(showOperatorDashboard)}>
      {showIncompleteSetupBanner && (
        <SetupIncompleteBanner
          message={localizeMessage('operator.welcome.setup_incomplete_banner')}
          actionLabel={localizeMessage('operator.welcome.resume_setup')}
          onAction={() => history.push(`/app/${shopId}/setup`)}
        />
      )}

      {showOperatorDashboard ? (
        <OperatorDashboard
          operatorState={effectiveOperatorState}
          shopId={shopId}
          isLoading={isOperatorLoading}
          isServiceUpdating={isServiceUpdating}
          liveIndexing={liveIndexing}
          onStartIndexing={catalogSync.startFullUpdate}
          catalogSync={catalogSync.isDegraded ? null : catalogSync}
          pluginDiagnosticsSummary={pluginDiagnosticsSummary}
          pluginCapabilities={pluginSnapshot?.capabilities ?? null}
          onPublish={artifactMutationSupported ? startPublish : null}
          trialChip={trialChip}
        />
      ) : (
        <SetupWelcome
          operatorState={effectiveOperatorState}
          shopId={shopId}
          isLoading={isOperatorLoading}
          checklist={checklist}
          onPublish={artifactMutationSupported ? startPublish : null}
        />
      )}

      {artifactMutationSupported && <PublishProgressDialog
        open={publishDialog.open}
        operation={publishBuild.operation || 'production'}
        status={publishDialogStatus}
        error={publishBuild.error}
        onRetry={startPublish}
        onClose={publishDialog.close}
        localizeMessage={localizeMessage}
      />}
    </div>
  );
};

export default HomeScreen;
